/**
 * Brand onboarding — pure helpers (no React, no API). Tested in onboardingModel.test.ts.
 *
 * The starter batch: the « Vos prompts » step generates a few prompts per axis, then has to
 * land them on a cadence the brand's monthly allowance can sustain — a self-serve save that
 * does not fit is refused whole (409 ALLOWANCE_EXCEEDED). Owner 2026-11-03: rather many
 * prompts every few days than fewer daily, so the cadence slows before the batch shrinks.
 */
import type { BrandQueryAxis, Cadence } from '@traaker/shared';
import { agencyPromptCreditsPerMonth, launchesInPeriod } from '@traaker/shared';

/** Starter prompts generated per axis on the « Vos prompts » step. */
export const STARTER_COUNTS: Record<BrandQueryAxis, number> = {
  narrative: 4,
  authority: 5,
  referral: 5,
  comparison: 3,
};

/** Agency engines on a new brand (owner 2026-11-03): Perplexity, ChatGPT, AI Overview (run-path ids). */
export const DEFAULT_AGENCY_ENGINES = ['perplexity', 'openai', 'ai_overview'];

/** Self-serve engines on a new brand (owner 2026-11-04): ChatGPT + AI Overview, the rest switched on by the user. */
export const DEFAULT_SELF_SERVE_ENGINES = ['openai', 'ai_overview'];

/** A self-serve starter never runs faster than every 2 days (owner 2026-11-04), even when daily would fit. */
const STARTER_FASTEST_DAYS = 2;

/** The self-serve cadence squares, fastest first (FooterSettingsPanel SELF_SERVE_PIPS). */
const SELF_SERVE_CADENCES: Cadence[] = [1, 2, 3, 4, 5, 6, 7].map((d) => ({ interval: d, unit: 'days' as const }));

/** The agency cadence squares without « Manuel », fastest first (FooterSettingsPanel AGENCY_PIPS). */
const AGENCY_CADENCES: Cadence[] = [
  { interval: 1, unit: 'days' },
  { interval: 2, unit: 'days' },
  { interval: 3, unit: 'days' },
  { interval: 5, unit: 'days' },
  { interval: 7, unit: 'days' },
  { interval: 14, unit: 'days' },
  { interval: 1, unit: 'months' },
];

const SLOWEST_SELF_SERVE = SELF_SERVE_CADENCES[SELF_SERVE_CADENCES.length - 1];

/**
 * The self-serve starter cadence: the fastest square from every 2 days on whose monthly need
 * (`perLaunch × launches`) fits the allowance; null when even the slowest square does not.
 * `-1` = unlimited → every 2 days.
 */
export function starterCadence(perLaunch: number, allowance: number): Cadence | null {
  const squares = SELF_SERVE_CADENCES.filter((c) => c !== 'off' && c.interval >= STARTER_FASTEST_DAYS);
  if (allowance === -1) return squares[0];
  return squares.find((c) => perLaunch * launchesInPeriod(c) <= allowance) ?? null;
}

/** The fastest agency cadence whose prompt credits per month fit the balance; else « Manuel ». */
export function fastestAffordableAgencyCadence(perLaunch: number, balance: number): Cadence {
  return AGENCY_CADENCES.find((c) => agencyPromptCreditsPerMonth({ perLaunch, cadence: c }) <= balance) ?? 'off';
}

/** How many rows fit the allowance at the slowest self-serve cadence, `enginesPerRow` requests each per launch. */
export function maxRowsAtSlowest(enginesPerRow: number, allowance: number): number {
  if (allowance === -1) return Number.MAX_SAFE_INTEGER;
  if (enginesPerRow <= 0) return Number.MAX_SAFE_INTEGER;
  return Math.max(0, Math.floor(allowance / (enginesPerRow * launchesInPeriod(SLOWEST_SELF_SERVE))));
}

/** Keep `max` items, taken round-robin across axes (in first-seen order) so no axis empties first. */
export function trimBalanced<T extends { axis?: string }>(items: T[], max: number): T[] {
  if (items.length <= max) return items;
  const byAxis = new Map<string, T[]>();
  for (const it of items) {
    const k = it.axis ?? '';
    if (!byAxis.has(k)) byAxis.set(k, []);
    byAxis.get(k)!.push(it);
  }
  const lanes = [...byAxis.values()];
  const kept: T[] = [];
  for (let i = 0; kept.length < max; i++) {
    let any = false;
    for (const lane of lanes) {
      if (i < lane.length) {
        any = true;
        if (kept.length < max) kept.push(lane[i]);
      }
    }
    if (!any) break;
  }
  return kept;
}

/** Same cadence (structural). */
export function sameCadence(a: Cadence | undefined, b: Cadence | undefined): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
